import { useCallback, useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import Offcanvas from "react-bootstrap/Offcanvas";
import BomTable from "./BomTable";
import {
  STATUSES,
  allParents,
  issueCounts,
  issueLabel,
  partColumn,
  partIssue,
  rowHasIssue,
  beforeOf,
  changedColumns,
  defaultExpanded,
  defaultVisibleColumns,
  distinctCount,
  loadVisibleColumns,
  orderedColumns,
  pathOf,
  roleIndex,
  rowName,
  saveVisibleColumns,
  statusLabel,
  treeColumn,
  typeCounts,
  visibleRows,
} from "./bomView";

// Small warning triangle for the NOT FOUND chip
const AlertMark = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

// Rows drawn at first; big tabs (Vars, wireBOM) show more on demand
const PAGE_SIZE = 500;
const MAX_TYPE_CHIPS = 8;

// Rows shown at first in a comparison: the differences, with their parents for context
const DEFAULT_SHOW = ["changed", "added", "deleted"];

// "show" URL value -> statuses shown; absent = the differences, "all" = every row
const parseShow = (value) => {
  if (!value) return DEFAULT_SHOW;
  if (value === "all") return STATUSES;
  const picked = value.split(",").filter((s) => STATUSES.includes(s));
  return picked.length > 0 ? picked : DEFAULT_SHOW;
};

const showValue = (statuses) => {
  if (statuses.length === STATUSES.length) return "all";
  const same = statuses.length === DEFAULT_SHOW.length && DEFAULT_SHOW.every((s) => statuses.includes(s));
  return same ? null : STATUSES.filter((s) => statuses.includes(s)).join(",");
};

// One tab of a BOM or of a comparison. sides ({ a, b } with shortLabel) is set in the comparison viewer; then the
// rows of a compared tab carry a status and the changed cells show both values.
export default function SectionView({ section, source, params, setParam, sides }) {
  const compared = !!sides && section.compared === true;
  const query = params.get("q") || "";
  const mode = section.isTree && params.get("view") !== "list" ? "tree" : "list";
  const typeFilter = params.get("type") || "";
  const showParam = params.get("show");
  const show = useMemo(() => (compared ? parseShow(showParam) : STATUSES), [compared, showParam]);
  // "Show them" in the NOT FOUND banner: only the rows with NOT FOUND or ERROR, whatever their status
  const issuesOnly = params.get("issues") === "1";
  const partCol = partColumn(section);
  const issueRows = useMemo(() => section.rows.filter((r) => rowHasIssue(section, r, partCol)).length, [section, partCol]);
  // Kinds found (NOT FOUND, ERROR or both) in either file, for the chip label
  const issueKinds = useMemo(() => {
    const a = issueCounts(section, compared ? "a" : undefined);
    const b = compared ? issueCounts(section, "b") : { notFound: 0, error: 0 };
    return { notFound: a.notFound + b.notFound, error: a.error + b.error };
  }, [section, compared]);

  // A comparison always starts with the columns that have changes, so no difference is hidden
  const [visible, setVisible] = useState(() => {
    const columns = loadVisibleColumns(source, section) || defaultVisibleColumns(section);
    if (!compared) return columns;
    const changed = changedColumns(section);
    return orderedColumns(section).filter((i) => columns.includes(i) || changed.has(i));
  });
  const [expanded, setExpanded] = useState(() => defaultExpanded(section));
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState(query);

  // Typing filters after a short pause so big tabs stay responsive
  useEffect(() => {
    const timer = setTimeout(() => setParam("q", search.trim() || null), 200);
    return () => clearTimeout(timer);
  }, [search, setParam]);

  useEffect(() => setLimit(PAGE_SIZE), [query, mode, typeFilter, showParam, issuesOnly]);

  const type = roleIndex(section, "type");
  const types = useMemo(() => typeCounts(section), [section]);
  const statusFilter = compared && !issuesOnly && show.length < STATUSES.length;
  const items = useMemo(() => {
    const byType = typeFilter && type >= 0 ? (row) => row.values[type] === typeFilter : null;
    // Rows of a repeated key have no status; they go with the unchanged ones
    const byStatus = statusFilter ? (row) => show.includes(row.status || "same") : null;
    const byIssue = issuesOnly ? (row) => rowHasIssue(section, row, partCol) : null;
    const checks = [byType, byStatus, byIssue].filter(Boolean);
    return visibleRows(section, {
      mode,
      query,
      expanded,
      searchColumns: section.columns.map((_, i) => i),
      filter: checks.length > 0 ? (row) => checks.every((check) => check(row)) : null,
    });
  }, [section, mode, query, expanded, typeFilter, type, statusFilter, show, issuesOnly, partCol]);
  const treeCol = treeColumn(section, visible);
  const parents = useMemo(() => allParents(section), [section]);
  const filtering = !!query || !!typeFilter || statusFilter || issuesOnly;
  const matchCount = filtering ? items.filter((i) => !i.context).length : null;

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // null = back to the default columns
  const changeColumns = (next) => {
    setVisible(next || defaultVisibleColumns(section));
    saveVisibleColumns(source, section, next);
  };

  const toggleStatus = (status) => {
    const next = show.includes(status) ? show.filter((s) => s !== status) : [...show, status];
    if (next.length > 0) setParam({ show: showValue(next), issues: null });
  };

  const rowClassName = useCallback((row) => (row.status ? `bom-row--${row.status}` : null), []);

  const levels = section.isTree ? Math.max(0, ...section.rows.map((r) => r.depth)) + 1 : null;
  const parts = distinctCount(section, "item");
  const clearFilters = () => {
    setSearch("");
    setParam({ type: null, issues: null, show: compared ? "all" : null });
  };

  return (
    <div className="card border-0 shadow-sm bom-viewer__panel">
      <div className="bom-viewer__toolbar">
        <input
          type="search"
          className="form-control form-control-sm bom-viewer__search"
          placeholder={section.isTree ? "Search part, bubble, description..." : "Search..."}
          aria-label={`Search in ${section.name}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {section.isTree && (
          <div className="btn-group btn-group-sm" role="group" aria-label="View">
            <button type="button" className={`btn btn-outline-secondary${mode === "tree" ? " active" : ""}`} aria-pressed={mode === "tree"} onClick={() => setParam("view", null)}>
              Tree
            </button>
            <button type="button" className={`btn btn-outline-secondary${mode === "list" ? " active" : ""}`} aria-pressed={mode === "list"} onClick={() => setParam("view", "list")}>
              List
            </button>
          </div>
        )}
        {mode === "tree" && !filtering && (
          <div className="btn-group btn-group-sm" role="group" aria-label="Expand">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setExpanded(new Set(parents))}>
              Expand all
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setExpanded(new Set())}>
              Collapse
            </button>
          </div>
        )}
        <ColumnPicker section={section} visible={visible} onChange={changeColumns} />
        <div className="bom-viewer__stats ms-md-auto">
          {matchCount != null ? (
            <span><b>{matchCount.toLocaleString()}</b> of {section.rows.length.toLocaleString()} rows</span>
          ) : (
            <span><b>{section.rows.length.toLocaleString()}</b> rows</span>
          )}
          {levels != null && <span>{levels} levels</span>}
          {parts > 0 && <span>{parts.toLocaleString()} part numbers</span>}
        </div>
      </div>

      {(compared || issueRows > 0) && (
        <div className="bom-viewer__types" role="group" aria-label="Show rows">
          {issueRows > 0 && (
            <button
              type="button"
              className={`bom-chip bom-chip--issue${issuesOnly ? " is-active" : ""}`}
              aria-pressed={issuesOnly}
              title="Part Number NOT FOUND or ERROR: never release this BOM"
              onClick={() => setParam("issues", issuesOnly ? null : "1")}
            >
              <AlertMark />
              {issueLabel(issueKinds)} <span className="bom-chip__count">{issueRows.toLocaleString()}</span>
            </button>
          )}
          {compared && STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              className={`bom-chip bom-chip--${status}${!issuesOnly && show.includes(status) ? " is-active" : ""}`}
              aria-pressed={!issuesOnly && show.includes(status)}
              onClick={() => toggleStatus(status)}
            >
              <span className={`bom-status bom-status--${status}`} aria-hidden="true" />
              {statusLabel(status, sides)} <span className="bom-chip__count">{(section.counts?.[status] ?? 0).toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}
      {sides && section.compared === false && (
        <div className="bom-viewer__hint">
          {section.onlyIn
            ? `This tab is only in ${sides[section.onlyIn].shortLabel}, so it isn't compared.`
            : "This tab isn't compared. It shows the rows of " + sides.a.shortLabel + "."}
        </div>
      )}

      {types.length > 1 && (
        <div className="bom-viewer__types" role="group" aria-label="Filter by type">
          <button type="button" className={`bom-chip${!typeFilter ? " is-active" : ""}`} aria-pressed={!typeFilter} onClick={() => setParam("type", null)}>
            All types
          </button>
          {types.slice(0, MAX_TYPE_CHIPS).map(([name, count]) => (
            <button
              key={name}
              type="button"
              className={`bom-chip${typeFilter === name ? " is-active" : ""}`}
              aria-pressed={typeFilter === name}
              onClick={() => setParam("type", typeFilter === name ? null : name)}
            >
              {name} <span className="bom-chip__count">{count}</span>
            </button>
          ))}
          {types.length > MAX_TYPE_CHIPS && (
            <select
              className="form-select form-select-sm bom-viewer__more-types"
              aria-label="Other types"
              value={types.slice(MAX_TYPE_CHIPS).some(([name]) => name === typeFilter) ? typeFilter : ""}
              onChange={(e) => setParam("type", e.target.value || null)}
            >
              <option value="">More types...</option>
              {types.slice(MAX_TYPE_CHIPS).map(([name, count]) => (
                <option key={name} value={name}>{name} ({count})</option>
              ))}
            </select>
          )}
        </div>
      )}

      {filtering && mode === "tree" && matchCount > 0 && (
        <div className="bom-viewer__hint">Parents of the matching rows are shown in grey.</div>
      )}

      <div className="bom-viewer__scroll">
        {section.rows.length === 0 ? (
          <div className="p-4 text-muted">This tab is empty.</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-muted">
            {compared && !query && !typeFilter && !show.includes("same") ? "No differences in this tab. " : "Nothing matches. "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={clearFilters}>
              {compared ? "Show every row" : "Clear the filters"}
            </button>
          </div>
        ) : (
          <BomTable
            section={section}
            columns={visible}
            items={items.slice(0, limit)}
            treeCol={treeCol}
            isTree={mode === "tree"}
            query={query}
            onToggle={toggle}
            selectedId={selected?.id}
            onSelect={setSelected}
            rowClassName={compared ? rowClassName : undefined}
            sides={compared ? sides : undefined}
          />
        )}
      </div>
      {items.length > limit && (
        <div className="bom-viewer__more">
          Showing {limit.toLocaleString()} of {items.length.toLocaleString()} rows.{" "}
          <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
            Show {Math.min(PAGE_SIZE, items.length - limit).toLocaleString()} more
          </button>
        </div>
      )}

      <RowDetails section={section} row={selected} onClose={() => setSelected(null)} onPick={setSelected} sides={compared ? sides : undefined} />
    </div>
  );
}

function ColumnPicker({ section, visible, onChange }) {
  const order = orderedColumns(section);
  const toggle = (i) => {
    const next = visible.includes(i) ? visible.filter((v) => v !== i) : order.filter((c) => c === i || visible.includes(c));
    if (next.length > 0) onChange(next);
  };
  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle size="sm" variant="outline-secondary" id={`columns-${section.name}`}>
        Columns ({visible.length}/{section.columns.length})
      </Dropdown.Toggle>
      <Dropdown.Menu className="bom-viewer__columns">
        <div className="d-flex justify-content-between px-3 pb-2 small">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onChange(order)}>Show all</button>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onChange(null)}>Default</button>
        </div>
        {order.map((i) => (
          <label key={i} className="dropdown-item d-flex align-items-center gap-2 small">
            <input type="checkbox" className="form-check-input m-0" checked={visible.includes(i)} onChange={() => toggle(i)} />
            {section.columns[i].label}
          </label>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

// Every value of the clicked row, with its parents and children. In a comparison, changed fields show both values.
function RowDetails({ section, row, onClose, onPick, sides }) {
  const path = row ? pathOf(section, row) : [];
  const children = row ? section.rows.filter((r) => r.parentId === row.id) : [];
  const status = sides && row?.status;
  const summary = {
    changed: row?.changes ? `${row.changes.length} ${row.changes.length === 1 ? "field differs" : "fields differ"} between ${sides?.b.shortLabel} and ${sides?.a.shortLabel}.` : null,
    added: `Only in ${sides?.a.shortLabel}.`,
    deleted: `Only in ${sides?.b.shortLabel}.`,
    same: `The same in ${sides?.a.shortLabel} and ${sides?.b.shortLabel}.`,
  }[status];
  // NOT FOUND / ERROR in this row's Part Number; for a changed row it can be Prd's value only
  const part = partColumn(section);
  const ownIssue = row && part >= 0 ? partIssue(row.values[part]) : null;
  const beforeIssue = row && part >= 0 && sides ? partIssue(beforeOf(row, part)) : null;
  const issue = ownIssue || beforeIssue;
  const issueOnlyInB = (!ownIssue && !!beforeIssue) || (!!sides && row?.status === "deleted");
  return (
    <Offcanvas show={!!row} onHide={onClose} placement="end" className="bom-viewer__details">
      {row && (
        <>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="h6 mb-0">{rowName(section, row)}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="pt-0">
            {status && (
              <div className={`bom-viewer__status-note bom-row--${status}`}>
                <span className={`bom-status bom-status--${status}`} aria-hidden="true" />
                <b>{statusLabel(status, sides)}</b> {summary}
              </div>
            )}
            {issue && (
              <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
                Part Number is <b>{issue}</b>
                {issueOnlyInB ? ` in ${sides.b.shortLabel}` : ""}. A BOM with this part must never be released.
              </div>
            )}
            {path.length > 0 && (
              <nav aria-label="Parents" className="bom-viewer__path">
                {path.map((p) => (
                  <button key={p.id} type="button" className="btn btn-link btn-sm p-0" onClick={() => onPick(p)}>
                    {rowName(section, p)}
                  </button>
                ))}
              </nav>
            )}
            <dl className="bom-viewer__fields">
              {orderedColumns(section).map((i) => {
                const before = sides ? beforeOf(row, i) : undefined;
                return (
                  <div key={i} className={[!row.values[i] && before === undefined && "is-empty", before !== undefined && "is-changed"].filter(Boolean).join(" ") || undefined}>
                    <dt>{section.columns[i].label}</dt>
                    {before !== undefined ? (
                      <dd>
                        <span className="bom-diff__side">{sides.b.shortLabel}</span> <span className="bom-diff__before">{before || "(empty)"}</span>
                        <br />
                        <span className="bom-diff__side">{sides.a.shortLabel}</span> <span className="bom-diff__after">{row.values[i] || "(empty)"}</span>
                      </dd>
                    ) : (
                      <dd>{row.values[i] || "-"}</dd>
                    )}
                  </div>
                );
              })}
            </dl>
            {children.length > 0 && (
              <>
                <h6 className="bom-viewer__subtitle">Children ({children.length})</h6>
                <ul className="list-unstyled small mb-0">
                  {children.map((c) => (
                    <li key={c.id}>
                      <button type="button" className="btn btn-link btn-sm p-0 text-start" onClick={() => onPick(c)}>
                        {sides && c.status && c.status !== "same" && <span className={`bom-status bom-status--${c.status} me-1`} aria-label={statusLabel(c.status, sides)} />}
                        {rowName(section, c)}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Offcanvas.Body>
        </>
      )}
    </Offcanvas>
  );
}
